import { DEFAULT_TEMPLATE_ID, exportTemplates } from "./templateRegistry";
import type { ExportModuleType, ExportTemplateModule } from "./types";

export const CAMPAIGN_VISUALIZATION_SPEC_VERSION =
  "campaign-visualization/v1";
export const DEFAULT_VISUALIZATION_LAYOUT_KEY = "widescreen-export";
export const SUPPORTED_VISUALIZATION_LAYOUTS = [
  DEFAULT_VISUALIZATION_LAYOUT_KEY,
] as const;

export type JsonPrimitive = string | number | boolean | null;
export type JsonValue = JsonPrimitive | JsonValue[] | JsonObject;
export interface JsonObject {
  [key: string]: JsonValue;
}

export type VisualizationLayoutKey =
  (typeof SUPPORTED_VISUALIZATION_LAYOUTS)[number];

export type CampaignVisualizationReportSource =
  | {
      kind: "browser-saved-report";
      reportId: string;
    }
  | {
      kind: "artifact-run";
      runId: string;
      reportPath?: string;
    }
  | {
      kind: "inline-report";
      label?: string;
    };

export interface CampaignVisualizationLayoutSpec {
  key: VisualizationLayoutKey;
  templateId: string;
}

export interface CampaignVisualizationModuleSpec {
  id: string;
  type: ExportModuleType;
  title?: string;
  span?: number;
  options?: JsonObject;
}

export interface CampaignVisualizationSpec {
  schemaVersion: typeof CAMPAIGN_VISUALIZATION_SPEC_VERSION;
  reportType: "campaign";
  title: string;
  reportSource: CampaignVisualizationReportSource;
  layout: CampaignVisualizationLayoutSpec;
  modules: CampaignVisualizationModuleSpec[];
  notes?: string[];
  metadata?: JsonObject;
}

export interface CampaignVisualizationModuleInput {
  id?: string;
  type: ExportModuleType | string;
  title?: string;
  span?: number;
  options?: JsonObject;
}

export interface CreateCampaignVisualizationSpecInput {
  title: string;
  reportSource: CampaignVisualizationReportSource;
  layout?: Partial<CampaignVisualizationLayoutSpec>;
  modules?: CampaignVisualizationModuleInput[];
  notes?: string[];
  metadata?: JsonObject;
}

export type VisualizationSpecValidationCode =
  | "duplicate_module_id"
  | "invalid_json"
  | "invalid_type"
  | "missing_field"
  | "unsupported_layout"
  | "unsupported_module"
  | "unsupported_report_source"
  | "unsupported_report_type"
  | "unsupported_schema_version"
  | "unsupported_template";

export interface VisualizationSpecValidationError {
  path: string;
  code: VisualizationSpecValidationCode;
  message: string;
}

export type VisualizationSpecValidationResult =
  | { ok: true; spec: CampaignVisualizationSpec }
  | { ok: false; errors: VisualizationSpecValidationError[] };

export const SUPPORTED_VISUALIZATION_TEMPLATE_IDS = Object.freeze(
  Object.keys(exportTemplates).sort(),
);

export const SUPPORTED_VISUALIZATION_MODULE_TYPES: readonly ExportModuleType[] =
  Object.freeze(
    Array.from(
      new Set(
        Object.values(exportTemplates).flatMap((template) =>
          template.modules.map((module) => module.type),
        ),
      ),
    ).sort(),
  );

const SUPPORTED_VISUALIZATION_REPORT_SOURCES = [
  "artifact-run",
  "browser-saved-report",
  "inline-report",
] as const;

const supportedLayoutSet = new Set<string>(SUPPORTED_VISUALIZATION_LAYOUTS);
const supportedModuleTypeSet = new Set<string>(
  SUPPORTED_VISUALIZATION_MODULE_TYPES,
);
const supportedReportSourceSet = new Set<string>(
  SUPPORTED_VISUALIZATION_REPORT_SOURCES,
);

const templateList = SUPPORTED_VISUALIZATION_TEMPLATE_IDS.join(", ");
const moduleList = SUPPORTED_VISUALIZATION_MODULE_TYPES.join(", ");
const layoutList = SUPPORTED_VISUALIZATION_LAYOUTS.join(", ");

export function createCampaignVisualizationSpec(
  input: CreateCampaignVisualizationSpecInput,
): VisualizationSpecValidationResult {
  const templateId =
    typeof input.layout?.templateId === "string" &&
    input.layout.templateId.trim()
      ? input.layout.templateId.trim()
      : DEFAULT_TEMPLATE_ID;
  const template = isSupportedVisualizationTemplate(templateId)
    ? exportTemplates[templateId]
    : exportTemplates[DEFAULT_TEMPLATE_ID];
  const modules = input.modules ?? template.modules.map(moduleFromTemplate);

  return validateCampaignVisualizationSpec({
    schemaVersion: CAMPAIGN_VISUALIZATION_SPEC_VERSION,
    reportType: "campaign",
    title: typeof input.title === "string" ? input.title.trim() : input.title,
    reportSource: input.reportSource,
    layout: {
      key:
        typeof input.layout?.key === "string" && input.layout.key.trim()
          ? input.layout.key.trim()
          : DEFAULT_VISUALIZATION_LAYOUT_KEY,
      templateId,
    },
    modules: modules.map(normalizeModuleInput),
    ...(input.notes !== undefined ? { notes: normalizeStringList(input.notes) } : {}),
    ...(input.metadata !== undefined ? { metadata: input.metadata } : {}),
  });
}

export function validateCampaignVisualizationSpec(
  value: unknown,
): VisualizationSpecValidationResult {
  const errors: VisualizationSpecValidationError[] = [];

  if (!isPlainRecord(value)) {
    return {
      ok: false,
      errors: [
        {
          path: "$",
          code: "invalid_type",
          message: "Visualization spec must be an object.",
        },
      ],
    };
  }

  validateSchemaVersion(value.schemaVersion, errors);
  validateReportType(value.reportType, errors);
  validateTitle(value.title, errors);
  validateReportSource(value.reportSource, errors);
  validateLayout(value.layout, errors);
  validateModules(value.modules, errors);
  validateOptionalStringArray(value.notes, "notes", errors);
  validateOptionalJsonObject(value.metadata, "metadata", errors);

  if (errors.length > 0) {
    return { ok: false, errors };
  }

  return {
    ok: true,
    spec: {
      schemaVersion: CAMPAIGN_VISUALIZATION_SPEC_VERSION,
      reportType: "campaign",
      title: (value.title as string).trim(),
      reportSource: normalizeReportSource(value.reportSource),
      layout: normalizeLayout(value.layout),
      modules: normalizeModules(value.modules),
      ...(value.notes !== undefined
        ? { notes: normalizeStringList(value.notes as string[]) }
        : {}),
      ...(value.metadata !== undefined
        ? { metadata: cloneJsonObject(value.metadata as JsonObject) }
        : {}),
    },
  };
}

export function isSupportedVisualizationTemplate(templateId: string): boolean {
  return Object.prototype.hasOwnProperty.call(exportTemplates, templateId);
}

export function isSupportedVisualizationModuleType(
  moduleType: string,
): moduleType is ExportModuleType {
  return supportedModuleTypeSet.has(moduleType);
}

function moduleFromTemplate(
  module: ExportTemplateModule,
): CampaignVisualizationModuleInput {
  return {
    id: module.id,
    type: module.type,
    ...(module.title !== undefined ? { title: module.title } : {}),
    ...(module.span !== undefined ? { span: module.span } : {}),
    ...(module.props !== undefined && isJsonObject(module.props)
      ? { options: cloneJsonObject(module.props) }
      : {}),
  };
}

function normalizeModuleInput(
  module: CampaignVisualizationModuleInput,
): Record<string, unknown> {
  const id =
    typeof module.id === "string" && module.id.trim()
      ? module.id.trim()
      : typeof module.type === "string"
        ? module.type.trim()
        : "";

  return {
    id,
    type: module.type,
    ...(typeof module.title === "string" && module.title.trim()
      ? { title: module.title.trim() }
      : {}),
    ...(module.span !== undefined ? { span: module.span } : {}),
    ...(module.options !== undefined ? { options: module.options } : {}),
  };
}

function validateSchemaVersion(
  schemaVersion: unknown,
  errors: VisualizationSpecValidationError[],
) {
  if (schemaVersion !== CAMPAIGN_VISUALIZATION_SPEC_VERSION) {
    errors.push({
      path: "schemaVersion",
      code: "unsupported_schema_version",
      message: `Expected schemaVersion "${CAMPAIGN_VISUALIZATION_SPEC_VERSION}".`,
    });
  }
}

function validateReportType(
  reportType: unknown,
  errors: VisualizationSpecValidationError[],
) {
  if (reportType !== "campaign") {
    errors.push({
      path: "reportType",
      code: "unsupported_report_type",
      message: 'Visualization specs currently support reportType "campaign" only.',
    });
  }
}

function validateTitle(
  title: unknown,
  errors: VisualizationSpecValidationError[],
) {
  if (!isNonEmptyString(title)) {
    errors.push({
      path: "title",
      code: "missing_field",
      message: "A visualization spec needs a non-empty title.",
    });
  }
}

function validateReportSource(
  reportSource: unknown,
  errors: VisualizationSpecValidationError[],
) {
  if (!isPlainRecord(reportSource)) {
    errors.push({
      path: "reportSource",
      code: "invalid_type",
      message: "reportSource must be an object.",
    });
    return;
  }

  if (!isNonEmptyString(reportSource.kind)) {
    errors.push({
      path: "reportSource.kind",
      code: "missing_field",
      message: "reportSource.kind is required.",
    });
    return;
  }

  if (!supportedReportSourceSet.has(reportSource.kind)) {
    errors.push({
      path: "reportSource.kind",
      code: "unsupported_report_source",
      message: `Unsupported report source "${reportSource.kind}". Supported sources: ${SUPPORTED_VISUALIZATION_REPORT_SOURCES.join(
        ", ",
      )}.`,
    });
    return;
  }

  if (
    reportSource.kind === "browser-saved-report" &&
    !isNonEmptyString(reportSource.reportId)
  ) {
    errors.push({
      path: "reportSource.reportId",
      code: "missing_field",
      message: "browser-saved-report sources require a reportId.",
    });
  }

  if (
    reportSource.kind === "artifact-run" &&
    !isNonEmptyString(reportSource.runId)
  ) {
    errors.push({
      path: "reportSource.runId",
      code: "missing_field",
      message: "artifact-run sources require a runId.",
    });
  }

  if (
    reportSource.kind === "artifact-run" &&
    reportSource.reportPath !== undefined &&
    typeof reportSource.reportPath !== "string"
  ) {
    errors.push({
      path: "reportSource.reportPath",
      code: "invalid_type",
      message: "reportSource.reportPath must be a string when supplied.",
    });
  }

  if (
    reportSource.kind === "inline-report" &&
    reportSource.label !== undefined &&
    typeof reportSource.label !== "string"
  ) {
    errors.push({
      path: "reportSource.label",
      code: "invalid_type",
      message: "reportSource.label must be a string when supplied.",
    });
  }
}

function validateLayout(
  layout: unknown,
  errors: VisualizationSpecValidationError[],
) {
  if (!isPlainRecord(layout)) {
    errors.push({
      path: "layout",
      code: "invalid_type",
      message: "layout must be an object.",
    });
    return;
  }

  if (!isNonEmptyString(layout.key)) {
    errors.push({
      path: "layout.key",
      code: "missing_field",
      message: "layout.key is required.",
    });
  } else if (!supportedLayoutSet.has(layout.key)) {
    errors.push({
      path: "layout.key",
      code: "unsupported_layout",
      message: `Unsupported layout "${layout.key}". Supported layouts: ${layoutList}.`,
    });
  }

  if (!isNonEmptyString(layout.templateId)) {
    errors.push({
      path: "layout.templateId",
      code: "missing_field",
      message: "layout.templateId is required.",
    });
  } else if (!isSupportedVisualizationTemplate(layout.templateId)) {
    errors.push({
      path: "layout.templateId",
      code: "unsupported_template",
      message: `Unsupported template "${layout.templateId}". Supported templates: ${templateList}.`,
    });
  }
}

function validateModules(
  modules: unknown,
  errors: VisualizationSpecValidationError[],
) {
  if (!Array.isArray(modules)) {
    errors.push({
      path: "modules",
      code: "invalid_type",
      message: "modules must be an ordered array.",
    });
    return;
  }

  if (modules.length === 0) {
    errors.push({
      path: "modules",
      code: "missing_field",
      message: "At least one visualization module is required.",
    });
    return;
  }

  const ids = new Set<string>();

  modules.forEach((module, index) => {
    const path = `modules[${index}]`;
    if (!isPlainRecord(module)) {
      errors.push({
        path,
        code: "invalid_type",
        message: "Each module must be an object.",
      });
      return;
    }

    if (!isNonEmptyString(module.id)) {
      errors.push({
        path: `${path}.id`,
        code: "missing_field",
        message: "Each module needs a non-empty id.",
      });
    } else if (ids.has(module.id)) {
      errors.push({
        path: `${path}.id`,
        code: "duplicate_module_id",
        message: `Duplicate module id "${module.id}".`,
      });
    } else {
      ids.add(module.id);
    }

    if (!isNonEmptyString(module.type)) {
      errors.push({
        path: `${path}.type`,
        code: "missing_field",
        message: "Each module needs a non-empty type.",
      });
    } else if (!isSupportedVisualizationModuleType(module.type)) {
      errors.push({
        path: `${path}.type`,
        code: "unsupported_module",
        message: `Unsupported module "${module.type}". Supported modules: ${moduleList}.`,
      });
    }

    if (module.title !== undefined && typeof module.title !== "string") {
      errors.push({
        path: `${path}.title`,
        code: "invalid_type",
        message: "Module title must be a string when supplied.",
      });
    }

    const span = module.span;
    if (
      span !== undefined &&
      (typeof span !== "number" ||
        !Number.isInteger(span) ||
        span < 1 ||
        span > 12)
    ) {
      errors.push({
        path: `${path}.span`,
        code: "invalid_type",
        message: "Module span must be an integer from 1 to 12 when supplied.",
      });
    }

    validateOptionalJsonObject(module.options, `${path}.options`, errors);
  });
}

function validateOptionalStringArray(
  value: unknown,
  path: string,
  errors: VisualizationSpecValidationError[],
) {
  if (value === undefined) {
    return;
  }
  if (!Array.isArray(value)) {
    errors.push({
      path,
      code: "invalid_type",
      message: `${path} must be an array of strings when supplied.`,
    });
    return;
  }
  value.forEach((item, index) => {
    if (typeof item !== "string") {
      errors.push({
        path: `${path}[${index}]`,
        code: "invalid_type",
        message: `${path}[${index}] must be a string.`,
      });
    }
  });
}

function validateOptionalJsonObject(
  value: unknown,
  path: string,
  errors: VisualizationSpecValidationError[],
) {
  if (value === undefined) {
    return;
  }
  if (!isJsonObject(value)) {
    errors.push({
      path,
      code: "invalid_json",
      message: `${path} must be a JSON-serializable object.`,
    });
  }
}

function normalizeReportSource(
  source: unknown,
): CampaignVisualizationReportSource {
  const record = source as Record<string, unknown>;
  if (record.kind === "browser-saved-report") {
    return {
      kind: "browser-saved-report",
      reportId: (record.reportId as string).trim(),
    };
  }
  if (record.kind === "artifact-run") {
    return {
      kind: "artifact-run",
      runId: (record.runId as string).trim(),
      ...(typeof record.reportPath === "string" && record.reportPath.trim()
        ? { reportPath: record.reportPath.trim() }
        : {}),
    };
  }
  return {
    kind: "inline-report",
    ...(typeof record.label === "string" && record.label.trim()
      ? { label: record.label.trim() }
      : {}),
  };
}

function normalizeLayout(layout: unknown): CampaignVisualizationLayoutSpec {
  const record = layout as Record<string, unknown>;
  return {
    key: record.key as VisualizationLayoutKey,
    templateId: (record.templateId as string).trim(),
  };
}

function normalizeModules(modules: unknown): CampaignVisualizationModuleSpec[] {
  return (modules as Record<string, unknown>[]).map((module) => ({
    id: (module.id as string).trim(),
    type: module.type as ExportModuleType,
    ...(typeof module.title === "string" && module.title.trim()
      ? { title: module.title.trim() }
      : {}),
    ...(module.span !== undefined ? { span: module.span as number } : {}),
    ...(module.options !== undefined
      ? { options: cloneJsonObject(module.options as JsonObject) }
      : {}),
  }));
}

function normalizeStringList(values: string[]): string[] {
  return values.map((value) => value.trim()).filter(Boolean);
}

function isNonEmptyString(value: unknown): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    Object.getPrototypeOf(value) === Object.prototype
  );
}

function isJsonObject(value: unknown): value is JsonObject {
  return isJsonValue(value) && isPlainRecord(value);
}

function isJsonValue(
  value: unknown,
  seen: WeakSet<object> = new WeakSet(),
): value is JsonValue {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "boolean"
  ) {
    return true;
  }

  if (typeof value === "number") {
    return Number.isFinite(value);
  }

  if (Array.isArray(value)) {
    if (seen.has(value)) {
      return false;
    }
    seen.add(value);
    return value.every((item) => isJsonValue(item, seen));
  }

  if (isPlainRecord(value)) {
    if (seen.has(value)) {
      return false;
    }
    seen.add(value);
    return Object.values(value).every((item) => isJsonValue(item, seen));
  }

  return false;
}

function cloneJsonObject(value: JsonObject): JsonObject {
  return Object.entries(value).reduce<JsonObject>((clone, [key, entry]) => {
    clone[key] = cloneJsonValue(entry);
    return clone;
  }, {});
}

function cloneJsonValue(value: JsonValue): JsonValue {
  if (Array.isArray(value)) {
    return value.map(cloneJsonValue);
  }
  if (value !== null && typeof value === "object") {
    return cloneJsonObject(value);
  }
  return value;
}
